import { useState } from 'react';

import { Button, Drawer, Input, Switch } from '@iwoca/orion';
import { clsx } from 'clsx';

import {
  EXPORT_RANGE_PRESETS,
  ExportRangePreset,
  ExportRequest,
  resolvePresetRange,
  toUtcDay,
} from './exportDateRange';

const INVERTED_RANGE_ERROR = 'End date must be on or after the start date.';

// `bg-none` clears Orion's variant gradient; `!` beats its `:hover` selector.
const SELECTED_FILL = '!bg-structure-80 !bg-none';

export const ExportSettingsDrawer = ({
  isOpen,
  onClose,
  acrossBothTabs,
  onAcrossBothTabsChange,
  onExport,
}: {
  isOpen: boolean;
  onClose: () => void;
  acrossBothTabs: boolean;
  /** Committed on click, not on export: the toggle outlives this one export. */
  onAcrossBothTabsChange: (value: boolean) => void;
  onExport: (request: ExportRequest) => void;
}) => {
  // Staged locally and re-seeded each time the drawer opens, so dismissing it
  // discards the range (React's "adjust state on prop change" — no effect).
  const [preset, setPreset] = useState<ExportRangePreset>('all_time');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setPreset('all_time');
      setCustomFrom('');
      setCustomTo('');
    }
  }

  const today = toUtcDay(new Date());
  const isCustom = preset === 'custom';
  // `YYYY-MM-DD` sorts chronologically, so a string compare is a date compare.
  const isInverted = Boolean(customFrom && customTo && customTo < customFrom);
  const canExport =
    !isCustom || (Boolean(customFrom) && Boolean(customTo) && !isInverted);

  const exportNow = () =>
    onExport(
      preset === 'custom'
        ? { preset, dateFrom: customFrom, dateTo: customTo }
        : { preset, ...resolvePresetRange(preset, new Date()) },
    );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Export settings"
      subTitle={
        <span className="text-s text-structure-40">
          Customers are exported by their date of application.
        </span>
      }
    >
      {/* Orion's panel and child wrapper are the whole of the design's inset. */}
      <div className="flex h-full flex-col">
        {/* `-mx-s px-s` nets to zero; it stops the scroll container clipping the
            fields' focus ring (an `overflow-y` of `auto` clips x too). */}
        <div className="-mx-s flex flex-1 flex-col gap-xl overflow-y-auto px-s pb-[96px]">
          <div className="flex flex-col gap-l">
            <div
              role="group"
              aria-label="Date range"
              className="grid grid-cols-2 gap-l px-s"
            >
              {EXPORT_RANGE_PRESETS.map((option) => (
                <Button
                  key={option.value}
                  variant="secondary-alt"
                  noShadow
                  aria-pressed={preset === option.value}
                  onClick={() => setPreset(option.value)}
                  className={clsx({
                    [SELECTED_FILL]: preset === option.value,
                  })}
                >
                  {option.label}
                </Button>
              ))}
              {/* A fifth date-range option, so it belongs to the same group.
                  Spanning both columns is what puts it on its own centred row. */}
              <Button
                variant="tertiary"
                noShadow
                aria-pressed={isCustom}
                onClick={() => setPreset('custom')}
                className={clsx('col-span-2 justify-self-center', {
                  [SELECTED_FILL]: isCustom,
                })}
              >
                Custom range
              </Button>
            </div>

            {isCustom && (
              <>
                <Input
                  id="export-start-date"
                  type="date"
                  label="Start date"
                  max={today}
                  value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)}
                />
                <Input
                  id="export-end-date"
                  type="date"
                  label="End date"
                  max={today}
                  value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)}
                  error={isInverted ? INVERTED_RANGE_ERROR : undefined}
                  aria-invalid={isInverted || undefined}
                  // The error is absolutely positioned, so this is the space it
                  // would otherwise overlap into.
                  wrapperClassName={clsx({ 'mb-3xl': isInverted })}
                />
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-m rounded-s border border-structure-80 bg-structure-100 p-m">
            <span className="text-s font-med text-primary-10">
              Export across both tabs
            </span>
            <Switch
              aria-label="Export across both tabs"
              checked={acrossBothTabs}
              onClick={() => onAcrossBothTabsChange(!acrossBothTabs)}
            />
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 bg-structure-100 p-xl shadow-black-l">
          <Button className="w-full" disabled={!canExport} onClick={exportNow}>
            Export
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
